import React, { useCallback, useRef } from 'react'
import { PlateContent, PlateContentProps } from '@udecode/plate-common/react'
import { clsx } from 'clsx'
import { useDevice } from 'hooks/useDevice'
import * as fieldStyles from 'ui/components/_internal/Field/Field.css'

import * as styles from '../../RichTextEditor.css'

const inputClassName = clsx([fieldStyles.field, styles.richText, styles.contentEditable])

const Editor = React.forwardRef<
    HTMLDivElement,
    PlateContentProps & {
        customKeydownHandler?: (e: React.KeyboardEvent<HTMLDivElement>) => void
        hideAndroidPlaceholder?: () => void
        isEditing: boolean
    }
>(
    (
        {
            className,
            disabled,
            isEditing,
            readOnly,
            customKeydownHandler,
            onKeyDown,
            placeholder,
            hideAndroidPlaceholder,
            ...props
        },
        ref,
    ) => {
        const { isTouch, isAndroid } = useDevice()
        // Track composition state to prevent state updates during IME composition
        // which can cause duplicate text with keyboards like Sogou
        const isComposingRef = useRef(false)

        /**
         * We need to make sure `onKeyDown` passed as `PlateContentProps` is called as well, to ensure default behavior is intact
         */
        const _onKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLDivElement>) => {
                // Don't trigger state updates during composition to prevent duplicate text
                if (isAndroid() && !isComposingRef.current) {
                    hideAndroidPlaceholder?.()
                }
                customKeydownHandler?.(e)
                onKeyDown?.(e)
            },
            [onKeyDown, customKeydownHandler, hideAndroidPlaceholder, isAndroid],
        )

        const handleCompositionStart = useCallback(() => {
            isComposingRef.current = true
        }, [])

        const handleCompositionEnd = useCallback(() => {
            isComposingRef.current = false
            // Hide placeholder after composition ends (safe timing that won't interfere with IME)
            if (isAndroid()) {
                hideAndroidPlaceholder?.()
            }
        }, [isAndroid, hideAndroidPlaceholder])

        return (
            <div ref={ref}>
                <PlateContent
                    disableDefaultStyles
                    className={clsx(inputClassName, {
                        [styles.contentEditablePWA]: isTouch,
                        [styles.editMode]: isEditing,
                    })}
                    readOnly={disabled || readOnly}
                    aria-disabled={disabled}
                    spellCheck={!isTouch}
                    autoCapitalize="sentences"
                    autoComplete="off"
                    {...props}
                    /**
                     * @see showAndroidPlaceholder in `RichTextEditor.tsx` for why we need to conditionally pass placeholder
                     */
                    placeholder={isAndroid() ? undefined : placeholder}
                    onKeyDown={_onKeyDown}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                />
            </div>
        )
    },
)
Editor.displayName = 'Editor'

export { Editor }
